'use client'

import { useTranslation } from '@/hooks/client/use-translation'

import { PriceAlertSelection } from './prices/price-alert-selection'
import { PriceLadder } from './prices/price-ladder'
import { PriceModelTabs } from './prices/price-model-tabs'
import { PriceUpgradePanel } from './prices/price-upgrade-panel'
import { useProductPurchase } from './prices/purchase-context'
import { PurchaseSectionIntro } from './prices/purchase-section-intro'
import { SellerList } from './prices/seller-list'

/** The main purchasing surface reads the same selection and offers as the sidebar. */
export const PricesLadderSectionClient = () => {
  const purchase = useProductPurchase()
  const { t } = useTranslation()
  if (!purchase) {
    return null
  }
  const { data, selectedModel, selectedLevel, sellers, selectModel, selectLevel } = purchase
  const comparisons = selectedModel.levelData[selectedLevel.id]?.upgradeComparisons ?? []
  const hasModels = data.models.length > 1
  const hasLevels = data.models.some((model) => model.levels.length > 1)
  const canCompareLevels = selectedModel.levels.length > 1 && comparisons.length > 0
  return (
    <div>
      <h2 className="text-navy text-[24px] leading-tight font-medium lg:text-[2rem]">
        {t('product.purchase.title', 'Compare prices & care')}
      </h2>
      <PurchaseSectionIntro hasModels={hasModels} hasLevels={hasLevels} />
      {selectedModel.levels.length > 1 && (
        <div id="purchase-configuration" className="mt-5 scroll-mt-28">
          <PriceLadder levels={selectedModel.levels} selectedLevelId={selectedLevel.id} onSelect={selectLevel} />
        </div>
      )}
      <SellerList sellers={sellers} releaseSlug={data.releaseSlug} couponText={data.couponText} />
      {(canCompareLevels || hasModels) && (
        <details className="group/features mt-2">
          <summary
            data-tracking="ignore"
            className="flex min-h-11 cursor-pointer list-none items-center gap-2 text-sm font-semibold text-lapis hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lapis [&::-webkit-details-marker]:hidden"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              className="shrink-0 group-open/features:rotate-90"
            >
              <path d="m9 5 7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {hasModels
              ? hasLevels
                ? t('product.purchase.compareModelsAndLevels', 'Compare models and technology levels')
                : t('product.purchase.compareModels', 'Compare models')
              : t('product.purchase.compareFeatures', 'Compare technology level features')}
          </summary>
          {hasModels && (
            <div className="mt-1 mb-3">
              <PriceModelTabs models={data.models} selectedModelId={selectedModel.id} onSelect={selectModel} />
            </div>
          )}
          {canCompareLevels && (
            <PriceUpgradePanel
              key={`features:${selectedModel.id}:${selectedLevel.id}`}
              selectedLevel={selectedLevel}
              levels={selectedModel.levels}
              comparisons={comparisons}
              tooltips={data.tooltips}
            />
          )}
        </details>
      )}
      <PriceAlertSelection key={`alert:${selectedModel.id}:${selectedLevel.id}`} />
    </div>
  )
}
